import React, { lazy, useCallback, useEffect, useMemo } from 'react';
import { useIsMobile } from '../../../helpers/useIsMobile';
import { usePrescriptionStore } from '../../../stores/prescriptionStore';
import { HEADER_HEIGHT, HEADER_HEIGHT_MOBILE } from '../../../contants/size';
import { get } from 'lodash';
import withSuspense from '../../../components/withLazyComponent';
import BannerComponent from '../../../components/BannerComponent';
import { RQE } from '../../../interfaces/users';
import { useDrawerStore } from '../../../stores/drawerStore';
import { PartnerLogo } from './styles';

const GameficationHeader = withSuspense(lazy(() => import('./Gamefication/GameficationHeader')));

interface PrescriptionHeaderProps {
  showMenu?: boolean;
  onOpenHamburger?: () => void;
}

function HamburgerIcon() {
  return (
    <div className="flex flex-col gap-[5px] items-center justify-center w-5 h-5">
      <span className="block w-[18px] h-[2px] bg-primary rounded-sm" />
      <span className="block w-[18px] h-[2px] bg-primary rounded-sm" />
      <span className="block w-[18px] h-[2px] bg-primary rounded-sm" />
    </div>
  );
}

function PrescriptionHeader(props: PrescriptionHeaderProps) {
  const isMobile = useIsMobile();

  const validationCode = usePrescriptionStore((store) => store.validationCode());
  const benefit = usePrescriptionStore((store) => store.benefit());
  const logoUrl = usePrescriptionStore((store) => store.prescription?.LogoURL || null);
  const showGamefication = usePrescriptionStore((store) =>
    get(store, 'prescriberConfiguration.Features.enableGamefication', false)
  );

  const { prescription, prescriberConfiguration, setIsBannerShown } = usePrescriptionStore();
  const [shouldShowBanner, setShouldShowBanner] = React.useState<boolean>(false);

  const { setDrawerOpen, drawerOpen, setSelectedMenu } = useDrawerStore();

  useEffect(() => {
    const enableBanner = prescriberConfiguration?.Features?.enablePharmacyAds;
    const bannerPropsReady =
      prescriberConfiguration?.idParceiro !== undefined &&
      prescription?.Prescritor?.Especialidades !== undefined;

    if (enableBanner && bannerPropsReady) {
      setShouldShowBanner(true);
      setIsBannerShown(true);
    } else {
      setShouldShowBanner(false);
      setIsBannerShown(false);
    }
  }, [prescriberConfiguration, prescription, setIsBannerShown]);

  const closeBanner = useCallback(() => {
    setShouldShowBanner(false);
    setIsBannerShown(false);
  }, [setIsBannerShown]);

  const patientName = useMemo(() => {
    if (!prescription?.Paciente) return '';
    return prescription.Paciente.NomeSocial || prescription.Paciente.Nome || '';
  }, [prescription]);

  const handleHamburgerClick = useCallback(() => {
    if (props.onOpenHamburger) {
      props.onOpenHamburger();
    } else {
      if (!drawerOpen) setSelectedMenu('');
      setDrawerOpen(!drawerOpen);
    }
  }, [props, setDrawerOpen, drawerOpen, setSelectedMenu]);

  const headerHeight = isMobile ? HEADER_HEIGHT_MOBILE : HEADER_HEIGHT;

  return (
    <div>
      {isMobile && shouldShowBanner && (
        <BannerComponent
          closeBanner={closeBanner}
          partnerId={prescriberConfiguration?.idParceiro as number}
          partnerName={prescriberConfiguration?.Nome}
          establishmentName={prescription?.Estabelecimento?.Nome}
          patientAge={prescription?.Paciente?.Idade as string}
          doctorSpecialities={prescription?.Prescritor?.RQEs as RQE[]}
          prescriptionId={prescription?.idPrescricao}
          prescriberId={prescription?.idPrescritor}
          validationCode={prescription?.CodigoValidacao}
        />
      )}

      <div
        className="flex items-center justify-between bg-white border-b border-gray-lighter px-3 gap-6"
        style={{ height: headerHeight }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="min-w-0">
            <p className="text-[12px] text-gray-medium leading-[14px] font-normal m-0 whitespace-nowrap">
              Paciente
              {benefit && (
                <span className="text-[#007CFF] text-[12px] font-medium"> {benefit.NomeBeneficio}</span>
              )}
            </p>
            <p
              className="text-[16px] font-semibold text-ink leading-[18px] m-0 overflow-hidden text-ellipsis whitespace-nowrap max-w-[120px] sm:max-w-[200px]"
              title={patientName}
            >
              {patientName}
            </p>
          </div>

          <div className="w-px h-8 bg-gray-lighter shrink-0" />

          <div className="shrink-0">
            <p className="text-[12px] text-gray-medium leading-[14px] font-normal m-0">Receita</p>
            <p className="text-[16px] font-bold text-ink leading-[18px] m-0 font-mono">
              {validationCode}
            </p>
          </div>

          {logoUrl && (
            <>
              <div className="w-px h-8 bg-gray-lighter shrink-0" />
              <PartnerLogo src={logoUrl} alt="Logo da instituição" />
            </>
          )}
        </div>

        {!isMobile && shouldShowBanner && (
          <div className="flex-1 flex justify-center min-w-0">
            <BannerComponent
              closeBanner={closeBanner}
              partnerId={prescriberConfiguration?.idParceiro as number}
              partnerName={prescriberConfiguration?.Nome}
              establishmentName={prescription?.Estabelecimento?.Nome}
              patientAge={prescription?.Paciente?.Idade as string}
              doctorSpecialities={prescription?.Prescritor?.RQEs as RQE[]}
              prescriptionId={prescription?.idPrescricao}
              prescriberId={prescription?.idPrescritor}
              validationCode={prescription?.CodigoValidacao}
            />
          </div>
        )}

        <div className="flex items-center gap-2 shrink-0">
          {showGamefication && <GameficationHeader />}

          {props.showMenu !== false && (
            <button
              aria-label="Abrir menu"
              onClick={handleHamburgerClick}
              className="flex items-center justify-center w-9 h-9 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer bg-transparent border-0 p-0"
            >
              <HamburgerIcon />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default React.memo(PrescriptionHeader);
